'use client';

import {
  FactSlogan,
  freddyTheme,
  GridItem,
  IconButton,
  Input,
  InputAdornment,
  Link as FreddyLink
} from '@coolblue-development/freddy';
import buttonInteractionTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentInteraction/buttonInteractionTrigger';
import searchInteractionTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentInteraction/searchInteractionTrigger';
import { ArrowRight, Magnifier } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { useTranslation } from '@i18n/useTranslation';
import { SEARCH_QUERY_PARAM } from '@utils/constants';
import { useClientUrl } from '@utils/url-generators/useClientUrl';
import Link from 'next/link';
import React, { useRef, useState } from 'react';

export const HeroCard = () => {
  const { t } = useTranslation('homePage');
  const { trackEvent } = useAnalytics();
  const clientLogger = useClientLogger();

  const filterPageUrl = useClientUrl('filterPage')!;

  const [searchValue, setSearchValue] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const isNavigatingRef = useRef(false);

  const executeNavigation = (url: string) => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;
    setIsNavigating(true);
    try {
      globalThis.location.assign(url);
    } catch (error) {
      isNavigatingRef.current = false;
      setIsNavigating(false);
      clientLogger.error('Failed to navigate to URL', error as Error, { url });
    }
  };

  const buildSearchUrl = (query: string) => {
    const queryString = query.length
      ? `?${new URLSearchParams([[SEARCH_QUERY_PARAM, query]]).toString()}`
      : '';
    return `${filterPageUrl}${queryString}`;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isNavigatingRef.current) {
      trackEvent(searchInteractionTrigger({ search_term: searchValue, method: 'enter' }));
      executeNavigation(buildSearchUrl(searchValue));
    }
  };

  const handleClickAllVacancies = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isNavigatingRef.current) return;
    trackEvent(
      buttonInteractionTrigger({
        content: t('heroCard.allVacanciesLink'),
        sub_feature: 'all_vacancies'
      })
    );
    executeNavigation(filterPageUrl);
  };

  return (
    <GridItem
      backgroundColor={freddyTheme.color.background.base}
      padding={{
        xs: freddyTheme.spacing[400],
        md: `${freddyTheme.spacing[400]} ${freddyTheme.spacing[500]}`,
        lg: freddyTheme.spacing[700],
        xl: freddyTheme.spacing[1000]
      }}
      sx={{
        height: '140px',
        [freddyTheme.breakpoint.minWidth('lg')]: {
          gridColumn: '2 / 12',
          height: 'auto',
          zIndex: 1,
          boxShadow: freddyTheme.shadow.strong,
          borderRadius: freddyTheme.borderRadius[200]
        }
      }}
    >
      <FactSlogan
        fact={t('heroCard.fact')}
        slogan={t('heroCard.slogan')}
        size={{
          xs: 'small',
          md: 'medium',
          xl: 'xlarge'
        }}
        sx={{
          paddingBottom: freddyTheme.spacing[400],
          [freddyTheme.breakpoint.minWidth('lg')]: { paddingBottom: freddyTheme.spacing[600] }
        }}
      />
      <Input
        type='search'
        value={searchValue}
        onChange={e => setSearchValue(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        disabled={isNavigating}
        endAdornment={
          <InputAdornment>
            <IconButton
              onClick={() => {
                if (isNavigatingRef.current) return;
                trackEvent(searchInteractionTrigger({ search_term: searchValue, method: 'click' }));
                executeNavigation(buildSearchUrl(searchValue));
              }}
              iconColor='inverse'
              aria-label='Search'
              disabled={isNavigating}
              sx={{
                backgroundColor: freddyTheme.color.background.primary,
                borderRadius: `0 ${freddyTheme.borderRadius[100]} ${freddyTheme.borderRadius[100]} 0`
              }}
            >
              <Magnifier />
            </IconButton>
          </InputAdornment>
        }
        placeholder={t('heroCard.searchPlaceholder')}
      />
      <FreddyLink
        component={Link}
        startIcon={<ArrowRight />}
        marginTop={freddyTheme.spacing[400]}
        href={filterPageUrl}
        prefetch={false}
        onClick={handleClickAllVacancies}
        aria-label={t('heroCard.allVacanciesLink')}
      >
        {t('heroCard.allVacanciesLink')}
      </FreddyLink>
    </GridItem>
  );
};
